import { HttpResponseError, XMLHttpRequestError } from 'lib/error/http-response-error';
import { MicroserviceProxyClientErr } from './MicroserviceApi/MicroserviceProxyClient';
import { BirdProxyClientErr } from './BirdApi/BirdProxyClient';

type Violation = {
    message: string;
    propertyPath: string;
};

type HttpError = {
    code: number;
    message: string;
    detail?: string;
    violations?: Violation[];
    sentryPayload?: any;
    customText?: boolean;
    isUiAgnostic?: boolean;
    originalError?: any;
};

class ErrorService {
    protected errors: HttpError[];

    protected hasErrorCallback: (hasError: boolean) => void;

    constructor() {
        this.errors = [];
        this.hasErrorCallback = (): void => {};
    }

    public add(error: any): void {
        if (error instanceof HttpResponseError) {
            this.errors.push(getHttpError(error));
        } else if (error instanceof XMLHttpRequestError) {
            if (error.contentType?.includes('text/html') && error.server === 'awselb/2.0') {
                this.errors.push({
                    code: 403,
                    message: 'Your request has been blocked',
                    detail: 'Our apologies, your request has been blocked by our security firewall. Please try again. If error persists, please contact MrWork Support about this.',
                    sentryPayload: error.message,
                    customText: true,
                    originalError: error,
                });
            } else if (error.contentType?.includes('text/html')) {
                this.errors.push({
                    code: 911,
                    message: 'Unknown error',
                    sentryPayload: error.message,
                    originalError: error,
                });
            }
            this.errors.push({
                code: 0,
                message: 'Unknown error',
                sentryPayload: error.message,
                originalError: error,
            });
        } else if (error instanceof MicroserviceProxyClientErr || error instanceof BirdProxyClientErr) {
            this.errors.push({
                code: error.status as number,
                isUiAgnostic: true,
                message: error.message,
                detail: error.details,
                originalError: error,
                sentryPayload: {
                    message: error.message,
                    detail: error.details,
                    code: error.code,
                    status: error.status,
                    name: error.name,
                    originalError: error,
                },
            });
        } else {
            this.errors.push({
                code: 0,
                message: 'Unknown error',
                originalError: error,
            });
        }
        this.hasErrorCallback(true);
    }

    public add404(): void {
        this.errors.push({
            code: 404,
            message: 'Not found',
        });
        this.hasErrorCallback(true);
    }

    public add403(): void {
        this.errors.push({
            code: 403,
            message: 'Forbidden',
        });
        this.hasErrorCallback(true);
    }

    public show(): HttpError[] {
        return this.errors;
    }

    public clear(): void {
        this.errors = [];
        this.hasErrorCallback(false);
    }

    public setHasErrorCallback(hasErrorCallback: (hasError: boolean) => void): void {
        this.hasErrorCallback = hasErrorCallback;
    }
}

export const getHttpError = (error: HttpResponseError): HttpError => ({
    code: error.response!.status,
    message: error.resolvedResponseBody?.message ?? 'Unknown error ocurred',
    detail: error.resolvedResponseBody?.detail,
    violations: error.resolvedResponseBody?.violations,
    originalError: error,
});

export const errorService = new ErrorService();
